import { useNavigate } from "react-router-dom";
import "../index.css";
import Navbar from "../component/navbar/Navbar";
import Footer from "../screens/Footer/Footer";
import { useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import SearchBar from "../component/common/icon-searchbar/IconSearchBar";
import { BsArrowRight, BsCheck2, BsStarFill, BsShieldCheck, BsClock, BsChat } from "react-icons/bs";
import { FaQuoteLeft } from "react-icons/fa";

function HomeV3() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    { name: "Plumbing", icon: "🔧", bg: "#dbeafe" },
    { name: "Electrical", icon: "⚡", bg: "#fef3c7" },
    { name: "Cleaning", icon: "✨", bg: "#d1fae5" },
    { name: "Painting", icon: "🎨", bg: "#ede9fe" },
    { name: "HVAC", icon: "❄️", bg: "#e0f2fe" },
    { name: "Handyman", icon: "🔨", bg: "#fee2e2" },
    { name: "Landscaping", icon: "🌿", bg: "#dcfce7" },
    { name: "Moving", icon: "📦", bg: "#fce7f3" },
  ];

  return (
    <div style={{ backgroundColor: "#fff" }}>
      <Navbar />

      {/* Hero - Minimal Centered */}
      <Box sx={{ pt: { xs: 16, md: 20 }, pb: { xs: 10, md: 14 } }}>
        <Container maxWidth="md">
          <Stack alignItems="center" textAlign="center" spacing={4}>
            {/* Badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                border: "1px solid #e5e5e5",
                borderRadius: "50px",
                px: 2.5,
                py: 1,
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#10b981" }} />
              <Typography sx={{ fontSize: "14px", color: "#555" }}>
                500+ pros available now
              </Typography>
            </Box>

            {/* Main Headline */}
            <Typography
              sx={{
                fontSize: { xs: "42px", sm: "56px", md: "72px" },
                fontWeight: "700",
                color: "#111",
                lineHeight: 1.05,
                letterSpacing: "-0.03em",
              }}
            >
              Home help,
              <br />
              <Box component="span" sx={{ color: "#F15A24" }}>on demand.</Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "17px", md: "20px" },
                color: "#666",
                lineHeight: 1.6,
                maxWidth: "480px",
              }}
            >
              Book vetted professionals for any home project. Fast, reliable, guaranteed.
            </Typography>

            {/* Search */}
            <Box sx={{ width: "100%", maxWidth: "520px", mt: 2 }}>
              <SearchBar />
            </Box>

            {/* Trust line */}
            <Stack direction="row" spacing={4} sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BsShieldCheck size={16} color="#10b981" />
                <Typography sx={{ fontSize: "13px", color: "#888" }}>Insured</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BsClock size={16} color="#10b981" />
                <Typography sx={{ fontSize: "13px", color: "#888" }}>Same-day</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <BsCheck2 size={16} color="#10b981" />
                <Typography sx={{ fontSize: "13px", color: "#888" }}>Guaranteed</Typography>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Services - Horizontal Scroll on Mobile */}
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#fafafa" }}>
        <Container maxWidth="lg">
          <Typography
            sx={{
              fontSize: { xs: "13px" },
              fontWeight: "600",
              color: "#999",
              textTransform: "uppercase",
              letterSpacing: "2px",
              mb: 4,
              textAlign: "center",
            }}
          >
            Popular Services
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={6} sm={3} md={1.5} key={index}>
                <Box
                  onClick={() => navigate("/services")}
                  sx={{
                    backgroundColor: service.bg,
                    borderRadius: "16px",
                    py: 3,
                    px: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: "28px", mb: 1 }}>{service.icon}</Typography>
                  <Typography sx={{ fontSize: "13px", fontWeight: "600", color: "#333" }}>
                    {service.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works - Timeline Style */}
      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="sm">
          <Typography
            sx={{
              fontSize: { xs: "32px", md: "40px" },
              fontWeight: "700",
              color: "#111",
              textAlign: "center",
              mb: 8,
            }}
          >
            Book in 3 steps
          </Typography>

          <Stack spacing={0}>
            {[
              { num: "1", title: "Tell us what you need", desc: "Describe your project in a few words" },
              { num: "2", title: "Pick your pro", desc: "Browse profiles, reviews, and prices" },
              { num: "3", title: "Get it done", desc: "Sit back while we handle the rest" },
            ].map((step, index) => (
              <Stack key={index} direction="row" spacing={4}>
                {/* Timeline */}
                <Stack alignItems="center">
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      backgroundColor: index === 0 ? "#F15A24" : "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "18px",
                        fontWeight: "700",
                        color: index === 0 ? "#fff" : "#999",
                      }}
                    >
                      {step.num}
                    </Typography>
                  </Box>
                  {index < 2 && (
                    <Box
                      sx={{
                        width: 2,
                        height: 60,
                        backgroundColor: "#eee",
                        my: 1,
                      }}
                    />
                  )}
                </Stack>

                {/* Content */}
                <Stack sx={{ pt: 0.5, pb: index < 2 ? 4 : 0 }}>
                  <Typography sx={{ fontSize: "20px", fontWeight: "600", color: "#111", mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ fontSize: "15px", color: "#888", lineHeight: 1.5 }}>
                    {step.desc}
                  </Typography>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Stats - Simple Row */}
      <Box sx={{ py: 8, backgroundColor: "#111" }}>
        <Container maxWidth="md">
          <Grid container spacing={4}>
            {[
              { num: "10K+", label: "Jobs completed" },
              { num: "4.9", label: "Average rating" },
              { num: "500+", label: "Verified pros" },
              { num: "<2hr", label: "Avg response" },
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Stack alignItems="center">
                  <Typography
                    sx={{
                      fontSize: { xs: "32px", md: "42px" },
                      fontWeight: "700",
                      color: "#F15A24",
                    }}
                  >
                    {stat.num}
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: "rgba(255,255,255,0.6)" }}>
                    {stat.label}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonial - Single Large */}
      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="sm">
          <Stack alignItems="center" textAlign="center" spacing={4}>
            <FaQuoteLeft size={32} color="#eee" />
            <Typography
              sx={{
                fontSize: { xs: "22px", md: "28px" },
                fontWeight: "500",
                color: "#333",
                lineHeight: 1.5,
                fontStyle: "italic",
              }}
            >
              "I've tried other apps but Done is by far the best. Found a plumber in 10 minutes who fixed my leak the same day. Incredible service."
            </Typography>
            <Stack alignItems="center" spacing={1}>
              <Stack direction="row" spacing={0.5}>
                {[1,2,3,4,5].map(i => <BsStarFill key={i} size={14} color="#fbbf24" />)}
              </Stack>
              <Typography sx={{ fontWeight: "600", color: "#111" }}>Sarah M.</Typography>
              <Typography sx={{ fontSize: "13px", color: "#999" }}>Verified customer</Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Features - Grid */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: "#fafafa" }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {[
              { icon: <BsShieldCheck size={24} />, title: "Vetted pros", desc: "Every professional is background-checked and verified" },
              { icon: <BsClock size={24} />, title: "Fast booking", desc: "Get matched with available pros in minutes" },
              { icon: <BsStarFill size={24} />, title: "Quality work", desc: "Backed by our satisfaction guarantee" },
              { icon: <BsChat size={24} />, title: "Easy communication", desc: "Message your pro directly through the app" },
            ].map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Stack spacing={2}>
                  <Box sx={{ color: "#F15A24" }}>{feature.icon}</Box>
                  <Typography sx={{ fontSize: "18px", fontWeight: "600", color: "#111" }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ fontSize: "14px", color: "#666", lineHeight: 1.6 }}>
                    {feature.desc}
                  </Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA - Clean */}
      <Box sx={{ py: { xs: 10, md: 14 } }}>
        <Container maxWidth="sm">
          <Stack alignItems="center" textAlign="center" spacing={4}>
            <Typography
              sx={{
                fontSize: { xs: "32px", md: "44px" },
                fontWeight: "700",
                color: "#111",
                lineHeight: 1.1,
              }}
            >
              Ready to get started?
            </Typography>
            <Typography sx={{ fontSize: "17px", color: "#666" }}>
              Book your first service today.
            </Typography>
            <Box
              onClick={() => navigate("/services")}
              sx={{
                backgroundColor: "#F15A24",
                color: "#fff",
                px: 6,
                py: 2,
                borderRadius: "50px",
                fontWeight: "600",
                fontSize: "16px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#d94d1a",
                  gap: 2,
                },
              }}
            >
              Find a pro <BsArrowRight />
            </Box>
          </Stack>
        </Container>
      </Box>

      <Footer />
    </div>
  );
}

export default HomeV3;
